import { Head, router } from "@inertiajs/react"
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  Search,
  CheckCircle,
  XCircle,
  Users,
} from "lucide-react"
import { useState, useMemo } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { AssignGroupDialog } from "../components/AssignGroupDialog"
import { TrackFormDialog } from "./TrackFormDialog"
import { NewCourseDialog } from "./NewCourseDialog"
import { TrackCourseRows } from "./TrackCourseRows"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusBadge({ status }) {
  if (status === "published") {
    return (
      <Badge className="bg-green-500 text-white hover:bg-green-500">
        Published
      </Badge>
    )
  }
  return <Badge variant="secondary">Draft</Badge>
}

function CategoryPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors " +
        (active
          ? "bg-zinc-900 text-white border-zinc-900"
          : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50 hover:text-zinc-900")
      }
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Add Course Chooser Dialog
// ---------------------------------------------------------------------------

function AddCourseDialog({ open, onOpenChange, trackId, availableCourses = [] }) {
  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState(null)
  const [addingId, setAddingId] = useState(null)

  const categories = useMemo(() => {
    const map = new Map()
    availableCourses.forEach((c) => {
      if (c.category) map.set(c.category.id, c.category)
    })
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [availableCourses])

  const hasUncategorized = useMemo(
    () => availableCourses.some((c) => !c.category),
    [availableCourses]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return availableCourses.filter((c) => {
      if (categoryFilter === "__uncategorized__") {
        if (c.category) return false
      } else if (categoryFilter != null) {
        if (c.category?.id !== categoryFilter) return false
      }
      if (!q) return true
      return (
        c.title.toLowerCase().includes(q) ||
        c.category?.name?.toLowerCase().includes(q)
      )
    })
  }, [availableCourses, search, categoryFilter])

  const handleAdd = (courseId) => {
    setAddingId(courseId)
    router.post(
      `/app/admin/learning_tracks/${trackId}/track_courses`,
      { course_id: courseId },
      {
        preserveScroll: true,
        onFinish: () => setAddingId(null),
      }
    )
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setSearch("")
      setCategoryFilter(null)
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-display text-xl">
            Add Course
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Select a published course to add to this track.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="px-4 sm:px-0 pb-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search courses..."
              className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {(categories.length > 0 || hasUncategorized) && (
            <div className="flex flex-wrap gap-1.5">
              <CategoryPill
                active={categoryFilter === null}
                onClick={() => setCategoryFilter(null)}
              >
                All
              </CategoryPill>
              {categories.map((cat) => (
                <CategoryPill
                  key={cat.id}
                  active={categoryFilter === cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                >
                  {cat.name}
                </CategoryPill>
              ))}
              {hasUncategorized && (
                <CategoryPill
                  active={categoryFilter === "__uncategorized__"}
                  onClick={() => setCategoryFilter("__uncategorized__")}
                >
                  Uncategorized
                </CategoryPill>
              )}
            </div>
          )}
        </div>

        <div className="max-h-72 overflow-y-auto px-4 sm:px-0">
          {filtered.length > 0 ? (
            <div className="space-y-1">
              {filtered.map((course) => (
                <button
                  key={course.id}
                  onClick={() => handleAdd(course.id)}
                  disabled={addingId === course.id}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-zinc-900 block truncate">
                      {course.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {course.category && (
                      <Badge variant="outline" className="text-xs">
                        {course.category.name}
                      </Badge>
                    )}
                    {addingId === course.id && (
                      <span className="text-xs text-zinc-400">Adding...</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400">
              <BookOpen className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
              <p className="text-sm">
                {availableCourses.length === 0
                  ? "All published courses are already in this track."
                  : "No courses match your search or filter."}
              </p>
            </div>
          )}
        </div>

        <ResponsiveDialogFooter className="px-4 sm:px-0 pt-2">
          <ResponsiveDialogClose asChild>
            <Button type="button" variant="outline">
              Done
            </Button>
          </ResponsiveDialogClose>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function LearningTrackShow({
  currentUser,
  track,
  availableCourses,
  availableGroups = [],
  pendingCount,
  categories = [],
}) {
  const [newCourseOpen, setNewCourseOpen] = useState(false)
  const [expandedCourses, setExpandedCourses] = useState(() => new Set())
  const allExpanded = track.courses?.length > 0 && track.courses.every((c) => expandedCourses.has(c.trackCourseId))
  const toggleCourse = (id) =>
    setExpandedCourses((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  const toggleAll = () =>
    setExpandedCourses(allExpanded ? new Set() : new Set(track.courses.map((c) => c.trackCourseId)))
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [addCourseOpen, setAddCourseOpen] = useState(false)
  const [removeCourse, setRemoveCourse] = useState(null)
  const [publishing, setPublishing] = useState(false)
  const [assignGroupOpen, setAssignGroupOpen] = useState(false)
  const [removeGroup, setRemoveGroup] = useState(null)

  const handleRemoveGroup = () => {
    if (!removeGroup) return
    router.delete(
      `/app/admin/learning_tracks/${track.id}/group_assignments/${removeGroup.groupTrackId}`,
      {
        preserveScroll: true,
        onSuccess: () => setRemoveGroup(null),
      }
    )
  }

  const handleDeleteTrack = () => {
    router.delete(`/app/admin/learning_tracks/${track.id}`, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  const handleRemoveCourse = () => {
    if (!removeCourse) return
    router.delete(
      `/app/admin/learning_tracks/${track.id}/track_courses/${removeCourse.trackCourseId}`,
      {
        preserveScroll: true,
        onSuccess: () => setRemoveCourse(null),
      }
    )
  }

  const handleReorder = (course, newPosition) => {
    router.patch(
      `/app/admin/learning_tracks/${track.id}/track_courses/${course.trackCourseId}/reorder`,
      { position: newPosition },
      { preserveScroll: true }
    )
  }

  const handleTogglePublish = () => {
    setPublishing(true)
    const newStatus = track.status === "published" ? "draft" : "published"
    router.patch(
      `/app/admin/learning_tracks/${track.id}`,
      { status: newStatus },
      {
        preserveScroll: true,
        onFinish: () => setPublishing(false),
      }
    )
  }

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title={`${track.title} - Learning Tracks - Admin`} />

      <div className="space-y-6">
        {/* ---------------------------------------------------------------- */}
        {/* Section 1 -- Track Details                                       */}
        {/* ---------------------------------------------------------------- */}
        <div>
          <button
            onClick={() => router.visit("/app/admin/learning_tracks")}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Learning Tracks
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className="text-2xl font-bold text-zinc-900">
                  {track.title}
                </h1>
                <StatusBadge status={track.status} />
                {track.sequential && <Badge variant="outline">Sequential</Badge>}
                {track.certifying && <Badge variant="outline">Certifying</Badge>}
              </div>
              {track.description && (
                <p className="text-zinc-500 mt-1">{track.description}</p>
              )}
              <p className="text-sm text-zinc-400 mt-2">
                {track.coursesCount} course{track.coursesCount !== 1 && "s"} ·{" "}
                {track.groupsCount} group{track.groupsCount !== 1 && "s"}{" "}
                assigned
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant={track.status === "published" ? "outline" : "gradient"}
                size="sm"
                onClick={handleTogglePublish}
                disabled={publishing}
              >
                {publishing ? (
                  "Saving..."
                ) : track.status === "published" ? (
                  <>
                    <XCircle className="h-4 w-4 mr-1" />
                    Unpublish
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Publish
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setEditOpen(true)}
                title="Edit track"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDeleteOpen(true)}
                title="Delete track"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Section 2 -- Courses in Track                                    */}
        {/* ---------------------------------------------------------------- */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Courses
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                {track.courses?.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={toggleAll}>
                    {allExpanded ? "Hide lessons" : "Show all lessons"}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setAddCourseOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add existing course
                </Button>
                <Button variant="gradient" size="sm" onClick={() => setNewCourseOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  New course
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {track.courses?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500 w-20">Pos</TableHead>
                    <TableHead className="text-zinc-500">Course</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">
                      Category
                    </TableHead>
                    <TableHead className="text-zinc-500">Status</TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {track.courses.map((course) => (
                    <TrackCourseRows
                      key={course.trackCourseId}
                      course={course}
                      trackId={track.id}
                      expanded={expandedCourses.has(course.trackCourseId)}
                      onToggle={() => toggleCourse(course.trackCourseId)}
                      onReorder={handleReorder}
                      onRemove={setRemoveCourse}
                      statusBadge={<StatusBadge status={course.status} />}
                    />
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <BookOpen className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No courses in this track yet</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Add courses to build out this learning track.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Section 3 -- Group Assignment (#595)                             */}
        {/* ---------------------------------------------------------------- */}
        <Card className="bg-white border-zinc-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-zinc-900">
                Group Assignment
              </CardTitle>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setAssignGroupOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Assign Group
              </Button>
            </div>
            {(track.status !== "published" || track.certifying) && (
              <div className="space-y-1 text-sm text-zinc-500">
                {track.status !== "published" && (
                  <p>Members see this track only after you publish it.</p>
                )}
                {track.certifying && (
                  <p>
                    Coaches of a Certification group can also assign this
                    track in the coach portal. Other coaches cannot. Mark a
                    group as a Certification group on its group page.
                  </p>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {track.assignedGroups?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Group</TableHead>
                    <TableHead className="text-zinc-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {track.assignedGroups.map((group) => (
                    <TableRow
                      key={group.groupTrackId}
                      className="border-zinc-100 hover:bg-zinc-50"
                    >
                      <TableCell className="font-medium text-zinc-900">
                        {group.name}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRemoveGroup(group)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove from track"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-zinc-400">
                <Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No groups assigned</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Assign groups to make this track available to their members.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* Dialogs                                                              */}
      {/* -------------------------------------------------------------------- */}

      {/* Edit Track */}
      <TrackFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        track={track}
      />

      {/* Delete Track */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Learning Track</AlertDialogTitle>
            <AlertDialogDescription>
              {track.groupsCount > 0
                ? `This track is assigned to ${track.groupsCount} group${track.groupsCount === 1 ? "" : "s"}. Deleting it will remove access to its courses for members of those groups. This action cannot be undone.`
                : `Are you sure you want to delete "${track.title}"? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrack}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Track
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New course straight into this track */}
      <NewCourseDialog
        open={newCourseOpen}
        onOpenChange={setNewCourseOpen}
        trackId={track.id}
        trackTitle={track.title}
        categories={categories}
        defaultCategoryId={track.courses?.find((c) => c.category)?.category?.id}
      />

      {/* Add Course Chooser */}
      <AddCourseDialog
        open={addCourseOpen}
        onOpenChange={setAddCourseOpen}
        trackId={track.id}
        availableCourses={availableCourses}
      />

      {/* Assign Group */}
      <AssignGroupDialog
        open={assignGroupOpen}
        onOpenChange={setAssignGroupOpen}
        postUrl={`/app/admin/learning_tracks/${track.id}/group_assignments`}
        subject="track"
        availableGroups={availableGroups}
      />

      {/* Remove Group */}
      <AlertDialog
        open={!!removeGroup}
        onOpenChange={(open) => !open && setRemoveGroup(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Group</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <strong>{removeGroup?.name}</strong> from this track?
              Members of this group will no longer see it. Certificates
              already earned are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveGroup}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Course */}
      <AlertDialog
        open={!!removeCourse}
        onOpenChange={(open) => !open && setRemoveCourse(null)}
      >
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Course</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{removeCourse?.title}&rdquo; from this track? The
              course itself will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveCourse}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}
